// Logique pure des écrans des marchés : libellés des états, situation d'un emplacement sur le
// plan (filtres de l'écran S12), périodes par défaut, emprise d'une échelle pour les couches
// GeoJSON, marqueurs des cartes. Aucun montant n'est calculé ici (module des recettes).
import { PERIODICITES_REDEVANCE, ajouterJours, ajouterMois, formaterFrancs, type EtatAttribution, type EtatAutorisation, type EtatEmplacement, type PeriodiciteRedevance, type ProprietesCarteAutorisation, type ProprietesCarteMarche } from '@cc/contrats';
import type { MarqueurCarte, Territoire } from '@cc/ui';
import { empriseEchelle } from '@cc/ui';
import type { Attribution, CollectionCarte, Emplacement, SituationAttribution } from './types';

export const LIBELLES_ETAT_EMPLACEMENT: Record<EtatEmplacement, string> = {
  disponible: 'Disponible',
  attribue: 'Attribué',
  reserve: 'Réservé',
  hors_service: 'Hors service',
};

export const LIBELLES_ETAT_ATTRIBUTION: Record<EtatAttribution, string> = {
  attribuee: 'Attribuée',
  active: 'Active',
  resiliee: 'Résiliée',
  terminee: 'Terminée',
};

export const LIBELLES_SITUATION: Record<SituationAttribution, string> = {
  ...LIBELLES_ETAT_ATTRIBUTION,
  a_venir: 'À venir',
  en_cours: 'En cours',
  echue: 'Échue',
};

export const LIBELLES_ETAT_AUTORISATION: Record<EtatAutorisation, string> = {
  active: 'Active',
  expiree: 'Expirée',
  retiree: 'Retirée',
  annulee: 'Annulée',
};

export const LIBELLES_PERIODICITE: Record<PeriodiciteRedevance, string> = { mensuelle: 'Mensuelle', trimestrielle: 'Trimestrielle' };

export const LIBELLES_CANAL: Record<string, string> = { guichet: 'Saisie au guichet', dossier: 'Demande du portail', renouvellement: 'Renouvellement' };

/** Préavis de l'écran S12 : une attribution dont la fin tombe dans les 30 jours « expire sous 30 jours ». */
export const PREAVIS_JOURS = 30;

/** Situation d'un emplacement sur le plan : l'état, affiné pour les attribués dont la fin approche. */
export type SituationEmplacement = EtatEmplacement | 'expire_bientot';

export const LIBELLES_SITUATION_EMPLACEMENT: Record<SituationEmplacement, string> = {
  ...LIBELLES_ETAT_EMPLACEMENT,
  expire_bientot: 'Expire sous 30 jours',
};

/** Marque courte affichée sur la case du plan (écran S12 : « libre », « 30 j », « HS »). */
export const MARQUES_SITUATION: Record<SituationEmplacement, string> = {
  disponible: 'libre',
  attribue: '',
  reserve: 'rés.',
  hors_service: 'HS',
  expire_bientot: '30 j',
};

export function aujourdhuiIso(maintenant = new Date()): string {
  const m = String(maintenant.getMonth() + 1).padStart(2, '0');
  const j = String(maintenant.getDate()).padStart(2, '0');
  return `${maintenant.getFullYear()}-${m}-${j}`;
}

export function situationEmplacement(e: Pick<Emplacement, 'etat' | 'attribution_en_cours'>, aujourdhui = aujourdhuiIso()): SituationEmplacement {
  if (e.etat === 'attribue' && e.attribution_en_cours?.fin && e.attribution_en_cours.fin <= ajouterJours(aujourdhui, PREAVIS_JOURS)) return 'expire_bientot';
  return e.etat;
}

export type FiltreEmplacements = 'tous' | SituationEmplacement;

export const FILTRES_EMPLACEMENTS: { valeur: FiltreEmplacements; libelle: string }[] = [
  { valeur: 'tous', libelle: 'Tous' },
  { valeur: 'disponible', libelle: 'Disponibles' },
  { valeur: 'attribue', libelle: 'Attribués' },
  { valeur: 'expire_bientot', libelle: 'Expirent sous 30 jours' },
  { valeur: 'reserve', libelle: 'Réservés' },
  { valeur: 'hors_service', libelle: 'Hors service' },
];

/** Un emplacement « attribué » compte aussi ceux qui expirent bientôt. */
export function correspondFiltre(e: Pick<Emplacement, 'etat' | 'attribution_en_cours'>, filtre: FiltreEmplacements, aujourdhui = aujourdhuiIso()): boolean {
  if (filtre === 'tous') return true;
  const s = situationEmplacement(e, aujourdhui);
  if (filtre === 'attribue') return s === 'attribue' || s === 'expire_bientot';
  return s === filtre;
}

export function compterSituations(emplacements: Pick<Emplacement, 'etat' | 'attribution_en_cours'>[], aujourdhui = aujourdhuiIso()): Record<FiltreEmplacements, number> {
  const c: Record<FiltreEmplacements, number> = { tous: emplacements.length, disponible: 0, attribue: 0, expire_bientot: 0, reserve: 0, hors_service: 0 };
  for (const e of emplacements) {
    const s = situationEmplacement(e, aujourdhui);
    c[s] += 1;
    if (s === 'expire_bientot') c.attribue += 1;
  }
  return c;
}

/** Résumé d'occupation de l'en-tête S12 : « 12 occupés, soit 60 %. 7 disponibles, 1 hors service. » */
export function resumeOccupation(stats: { emplacements: number; utilisables: number; occupes: number; taux_occupation: number | null } | undefined, compte: Record<FiltreEmplacements, number>): string {
  if (!stats) return `${compte.tous} emplacement${compte.tous > 1 ? 's' : ''}.`;
  const taux = stats.taux_occupation === null ? '' : `, soit ${stats.taux_occupation.toLocaleString('fr-FR')} %`;
  return `${stats.occupes} occupé${stats.occupes > 1 ? 's' : ''}${taux}. ${compte.disponible} disponible${compte.disponible > 1 ? 's' : ''}, ${compte.reserve} réservé${compte.reserve > 1 ? 's' : ''}, ${compte.hors_service} hors service.`;
}

/** Période par défaut d'une attribution ou d'une simulation : une période de redevance à compter d'aujourd'hui. */
export function periodeParDefaut(periodicite: PeriodiciteRedevance, debut = aujourdhuiIso()): { debut: string; fin: string } {
  return { debut, fin: ajouterJours(ajouterMois(debut, PERIODICITES_REDEVANCE[periodicite]), -1) };
}

export function montant(valeur: number | null | undefined, fictif = false): string {
  if (valeur === null || valeur === undefined) return 'non renseigné';
  return `${formaterFrancs(valeur)}${fictif ? ' (fictif)' : ''}`;
}

/** Emprise de l'échelle affichée, au format bbox=ouest,sud,est,nord des routes GeoJSON ; null sans limite. */
export function bboxEchelle(t: Territoire | undefined): string | null {
  if (!t) return null;
  const e = empriseEchelle(t);
  return e ? e.map((v) => v.toFixed(6)).join(',') : null;
}

export function marqueursMarches(c: CollectionCarte<ProprietesCarteMarche> | undefined, depart = 0): MarqueurCarte[] {
  return (c?.features ?? [])
    .filter((f) => f.geometry.type === 'Point')
    .map((f, i) => ({
      id: f.properties.id,
      numero: depart + i + 1,
      longitude: (f.geometry as { coordinates: [number, number] }).coordinates[0],
      latitude: (f.geometry as { coordinates: [number, number] }).coordinates[1],
      libelle: `${f.properties.nom} · ${f.properties.occupes} occupé${f.properties.occupes > 1 ? 's' : ''} sur ${f.properties.emplacements}`,
      ton: 'ouvert' as const,
    }));
}

/** Marqueurs des autorisations : point représentatif de l'emprise ; les expirant sous 30 jours en « retard ». */
export function marqueursAutorisations(c: CollectionCarte<ProprietesCarteAutorisation> | undefined, depart = 0, aujourdhui = aujourdhuiIso()): MarqueurCarte[] {
  return (c?.features ?? []).map((f, i) => ({
    id: f.properties.id,
    numero: depart + i + 1,
    longitude: f.properties.centre.longitude,
    latitude: f.properties.centre.latitude,
    libelle: `${f.properties.numero} · ${f.properties.categorie_libelle} · ${f.properties.surface_m2.toLocaleString('fr-FR')} m² · jusqu'au ${f.properties.fin}`,
    ton: f.properties.fin <= ajouterJours(aujourdhui, PREAVIS_JOURS) ? ('retard' as const) : ('autre' as const),
  }));
}

/** Message lisible du refus d'une attribution (409 attribution_chevauchante et autres refus du serveur). */
export function expliquerRefusAttribution(code: string, message: string, details: unknown): string {
  if (code === 'attribution_chevauchante') {
    // Détails servis par attributions.service.ts : { attribution: numéro, periode: { debut, fin } de l'attribution en conflit }.
    const d = (details ?? {}) as { attribution?: string | { numero?: string }; periode?: { debut: string; fin: string } | null };
    const numero = typeof d.attribution === 'string' ? d.attribution : d.attribution?.numero;
    const conflit = d.periode ?? null;
    const qui = numero ? ` par l'attribution ${numero}` : '';
    const quand = conflit ? ` du ${conflit.debut} au ${conflit.fin}` : '';
    return `Chevauchement refusé : cet emplacement est déjà attribué${qui}${quand}. Choisissez une autre période ou un autre emplacement ; rien n'a été enregistré.`;
  }
  if (code === 'recettes_non_branchees') return 'Le module des recettes n\'est pas branché sur ce serveur : aucune redevance ne peut être calculée ni émise, l\'attribution est refusée.';
  if (code === 'tarif_introuvable') return 'Aucun tarif approuvé n\'est en vigueur pour la rubrique de cet emplacement à la date de début : faire approuver un tarif avant d\'attribuer.';
  if (code === 'rubrique_inactive') return 'La rubrique de redevance de cet emplacement est désactivée au catalogue des recettes.';
  return message;
}

export function libelleCommercant(a: Pick<Attribution, 'commercant'>): string {
  return `${a.commercant.nom}${a.commercant.type === 'entreprise' ? ' (entreprise)' : ''}${a.commercant.usager_compte_id ? ', compte portail' : ''}`;
}
